import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, Select, message, Upload, Spin } from 'antd';
import { ref, onValue, push } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import NegativeBalanceDrivers from '../components/NegativeBalanceDrivers';
import OpenChats from '../components/OpenChats';
import {
  Receipt,
  Gift,
  UploadSimple,
  CalendarBlank,
  CurrencyDollar,
  MapPin,
  FileText,
} from '@phosphor-icons/react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/pl';
import './Home.css';

dayjs.extend(isoWeek);
dayjs.locale('pl');

const { Option } = Select;
const { RangePicker } = DatePicker;

const rangePresets = [
  { label: 'Ostatnie 7 dni', value: [dayjs().subtract(7, 'day'), dayjs()] },
  { label: 'Ostatnie 14 dni', value: [dayjs().subtract(14, 'day'), dayjs()] },
  { label: 'Ostatnie 30 dni', value: [dayjs().subtract(30, 'day'), dayjs()] },
];

const CITIES = [
  'warszawa','krakow','wroclaw','poznan','gdansk','szczecin','lodz','lublin',
  'katowice','bialystok','gdynia','rzeszow','kalisz','kielce','sopot',
  'trojmiasto','opole','gorzow_wielkopolski','bielsko_biala','olsztyn','slupsk',
];

const Home = () => {
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;
  const { config } = currentFirebase;

  const [drivers, setDrivers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [expenseForm] = Form.useForm();
  const [bonusForm] = Form.useForm();
  const [isProjectChanging, setIsProjectChanging] = useState(false);

  useEffect(() => {
    setIsProjectChanging(true);
    const driversRef = ref(db, 'drivers');
    const unsub = onValue(driversRef, (snap) => {
      const raw = snap.val() || {};
      const list = Object.keys(raw).map((k) => ({ value: k, label: k }));
      setDrivers(list);
      setIsProjectChanging(false);
    }, () => setIsProjectChanging(false));
    return () => unsub();
  }, [db]);

  const driverSorted = [...drivers].sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  const handleExpenseSubmit = (values) => {
    const { driver, expenseType, grossAmount, purchaseDate } = values;
    push(ref(db, `drivers/${driver}/invoices`), {
      type: 'wydatek',
      numerfaktury: expenseType,
      grossAmount,
      purchaseDate: purchaseDate.format('YYYY-MM-DD'),
      status: 'zaakceptowany',
    }).then(() => { message.success('Wydatek dodany.'); expenseForm.resetFields(); })
      .catch(() => message.error('Błąd dodawania wydatku.'));
  };

  const handleBonusSubmit = (values) => {
    const { bonusDriver, bonusReason, bonusAmount, bonusDate, bonusComment } = values;
    push(ref(db, `drivers/${bonusDriver}/bonus_partnera`), {
      reason: bonusReason,
      amount: bonusAmount,
      date: bonusDate.format('YYYY-MM-DD'),
      comment: bonusComment || '',
    }).then(() => { message.success('Bonus dodany.'); bonusForm.resetFields(); })
      .catch(() => message.error('Błąd dodawania bonusu.'));
  };

  const handleUploadSubmit = async (values) => {
    const { fileType, city, dateRange } = values;
    if (fileList.length === 0) return message.error('Wybierz plik.');
    const [startDate, endDate] = dateRange;
    const file = fileList[0].originFileObj;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      const d = new Date(Date.UTC(startDate.year(), startDate.month(), startDate.date()));
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      const weekStr = weekNo + '-' + d.getUTCFullYear();

      message.loading('Wysyłanie...', 0);
      fetch(`https://us-central1-${config.projectId}.cloudfunctions.net/uploadCalculaet/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileType, week: weekStr, csvfile: base64, city,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        }),
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(() => { message.destroy(); message.success('Dane przetworzone!'); setFileList([]); })
        .catch(() => { message.destroy(); message.error('Błąd przetwarzania.'); });
    };
  };

  return (
    <div className="dash-page">
      {/* Row 1: Quick actions */}
      <div className="dash-grid-3">
        {/* Expense */}
        <div className="dash-card">
          <div className="dash-card-header">
            <Receipt size={18} weight="duotone" color="#2563eb" />
            <span className="dash-card-title">Dodaj wydatek</span>
          </div>
          <Form form={expenseForm} layout="vertical" onFinish={handleExpenseSubmit} className="dash-form">
            <Form.Item name="driver" rules={[{ required: true, message: 'Wybierz' }]}>
              <Select
                showSearch
                options={driverSorted}
                placeholder="Kierowca"
                optionFilterProp="label"
                filterOption={(i, o) => (o?.label || '').toLowerCase().includes(i.toLowerCase())}
              />
            </Form.Item>
            <Form.Item name="expenseType" rules={[{ required: true, message: 'Podaj typ' }]}>
              <Input placeholder="Typ wydatku" />
            </Form.Item>
            <div className="dash-form-row">
              <Form.Item name="grossAmount" rules={[{ required: true, message: 'Kwota' }]} style={{ flex: 1 }}>
                <Input type="number" step="0.01" placeholder="Kwota" />
              </Form.Item>
              <Form.Item name="purchaseDate" rules={[{ required: true, message: 'Data' }]} style={{ flex: 1 }}>
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Data" />
              </Form.Item>
            </div>
            <Button type="primary" htmlType="submit" block className="dash-btn">Dodaj wydatek</Button>
          </Form>
        </div>

        {/* Bonus */}
        <div className="dash-card">
          <div className="dash-card-header">
            <Gift size={18} weight="duotone" color="#7c3aed" />
            <span className="dash-card-title">Bonus partnera</span>
          </div>
          <Form form={bonusForm} layout="vertical" onFinish={handleBonusSubmit} className="dash-form">
            <div className="dash-form-row">
              <Form.Item name="bonusDriver" rules={[{ required: true, message: 'Wybierz' }]} style={{ flex: 1 }}>
                <Select
                  showSearch
                  options={driverSorted}
                  placeholder="Kierowca"
                  optionFilterProp="label"
                  filterOption={(i, o) => (o?.label || '').toLowerCase().includes(i.toLowerCase())}
                />
              </Form.Item>
              <Form.Item name="bonusReason" rules={[{ required: true, message: 'Powód' }]} style={{ flex: 1 }}>
                <Input placeholder="Powód" />
              </Form.Item>
            </div>
            <div className="dash-form-row">
              <Form.Item name="bonusAmount" rules={[{ required: true, message: 'Kwota' }]} style={{ flex: 1 }}>
                <Input type="number" step="0.01" placeholder="Kwota" />
              </Form.Item>
              <Form.Item name="bonusDate" rules={[{ required: true, message: 'Data' }]} style={{ flex: 1 }}>
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} placeholder="Data" />
              </Form.Item>
            </div>
            <Form.Item name="bonusComment">
              <Input placeholder="Komentarz (opcjonalnie)" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block className="dash-btn">Dodaj bonus</Button>
          </Form>
        </div>

        {/* Upload */}
        <div className="dash-card">
          <div className="dash-card-header">
            <UploadSimple size={18} weight="duotone" color="#16a34a" />
            <span className="dash-card-title">Rozlicz kierowców</span>
          </div>
          <Form layout="vertical" onFinish={handleUploadSubmit} className="dash-form">
            <div className="dash-form-row">
              <Form.Item name="fileType" rules={[{ required: true, message: 'Typ' }]} style={{ flex: 1 }}>
                <Select placeholder="Platforma">
                  <Option value="uber">Uber</Option>
                  <Option value="bolt">Bolt</Option>
                  <Option value="freenow">FreeNow</Option>
                </Select>
              </Form.Item>
              <Form.Item name="city" rules={[{ required: true, message: 'Miasto' }]} style={{ flex: 1 }}>
                <Select placeholder="Miasto" showSearch>
                  {CITIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </div>
            <Form.Item name="dateRange" rules={[{ required: true, message: 'Daty' }]}>
              <RangePicker presets={rangePresets} format="YYYY-MM-DD" style={{ width: '100%' }} placeholder={['Od', 'Do']} />
            </Form.Item>
            <Form.Item name="csvfile" rules={[{ required: true, message: 'Plik' }]}>
              <Upload beforeUpload={() => false} fileList={fileList} onChange={({ fileList: fl }) => setFileList(fl)} maxCount={1}>
                <Button block style={{ borderRadius: 8 }}>Wybierz plik</Button>
              </Upload>
            </Form.Item>
            <Button type="primary" htmlType="submit" block className="dash-btn">Rozlicz</Button>
          </Form>
        </div>
      </div>

      {/* Row 2: Widgets */}
      <div className="dash-grid-2">
        <NegativeBalanceDrivers />
        <OpenChats />
      </div>

      {isProjectChanging && (
        <div className="dash-overlay"><Spin size="large" /></div>
      )}
    </div>
  );
};

export default Home;
